/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, CircularProgress, Stack, TextField } from "@mui/material";

import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import StyledBreadcrumbs from "../../components/general/StyledBreadcrumbs";

import { useWindowDimensions } from "../../utils/Hooks";

import { Nullable, StorageLocationDetailed, TechwiseOffice } from "../../utils/Interfaces";
import { updateRestockingTasks } from "./materialsRestockingFunctions";
import { LocationType } from "../../utils/Constants";
import DataTable from "../../components/table/DataTable";
import { getInventoryCompilation } from "../../utils/Functions";
import { GridRenderCellParams } from "@mui/x-data-grid";
import Chip from "@mui/material/Chip";
import { selectExistingTechwiseOffices } from "../materials-techwise-offices/techwiseOfficesSlice";
import Autocomplete from "@mui/material/Autocomplete";
import { useAppDispatch, useAppSelector } from "../../app/hooks";

const OfficeRestocking = () => {
    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const { height } = useWindowDimensions();

    const techwiseOffices: TechwiseOffice[] = useAppSelector(selectExistingTechwiseOffices);
    const [techwiseOffice, setTechwiseOffice] = useState<Nullable<TechwiseOffice>>(null);
    const [storageLocations, setStorageLocations] = useState<StorageLocationDetailed[]>([]);

    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (techwiseOffices.length > 0) {
            setTechwiseOffice(techwiseOffices[0]);
        }
    }, [techwiseOffices]);

    useEffect(() => {
        if (techwiseOffice !== null) {
            updateRestockingTasks(techwiseOffice.id, LocationType.PERMANENT, dispatch, setStorageLocations, setLoading);
        }
    }, [techwiseOffice]);

    let columns: any[] = [
        {
            field: "storage_location_name",
            width: 200,
            headerName: "Location",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.storage_location.name;
            },
        },
        {
            field: "material_name",
            width: 300,
            headerName: "Part/Material Name",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.material.name;
            },
        },
        {
            field: "target",
            width: 100,
            headerName: "Target",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.target;
            },
        },
        {
            field: "amount",
            width: 100,
            headerName: "Amount",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.amount;
            },
        },
        {
            field: "operation",
            width: 180,
            headerName: "Operation",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.target - params.row.amount;
            },
            renderCell: (params: GridRenderCellParams) => {
                let delta: number = params.row.target - params.row.amount;

                return (
                    <Chip
                        color={delta > 0 ? "warning" : "success"}
                        label={delta > 0 ? `ORDER ${Math.abs(delta)}` : `EXCESS ${Math.abs(delta)}`}
                        style={{ fontWeight: "bold" }}
                    />
                );
            },
        },
    ];

    return (
        <Box>
            <StyledBreadcrumbs
                paths={[
                    { text: "Techwise Portal", route: "/" },
                    { text: "Materials", route: "/?section=materials" },
                    { text: "Office Restocking", route: "/materials/restocking/office" },
                ]}
            />

            <Box sx={{ p: 1, mt: 2 }}>
                <Autocomplete
                    disablePortal
                    onChange={(_event, value) => {
                        setTechwiseOffice(value ? value : techwiseOffices.length > 0 ? techwiseOffices[0] : null);
                    }}
                    id="techwiseOffices"
                    getOptionKey={(option: TechwiseOffice) => option.id}
                    options={techwiseOffices}
                    value={techwiseOffice}
                    getOptionLabel={(option: TechwiseOffice) => option.name}
                    renderInput={(params) => <TextField required {...params} label="Select Techwise Office" />}
                />
            </Box>

            {loading && (
                <Stack direction="row" justifyContent="center">
                    <CircularProgress size={40} sx={{ mt: 15, mb: 5 }} />
                </Stack>
            )}

            {!loading && (
                <Card sx={{ mt: 1 }}>
                    <CardContent>
                        <DataTable
                            tableName="OFFICE_RESTOCKING"
                            data={getInventoryCompilation(storageLocations)}
                            columns={columns}
                            height={height - 280}
                        />
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default OfficeRestocking;
